import React, { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { Sun, Moon, Monitor, Maximize2, Minimize2, Menu, X } from 'lucide-react';
import { DensityMode, ThemeMode } from '../types/api';
import { NotificationBell } from '../components/notification/NotificationBell';
import { Sidebar } from '../components/navigation/Sidebar';
import { useAuth } from '../context/AuthContext';
import { useNavigation } from '../context/NavigationContext';
import { AppTab } from '../config/navigation';
import { AdminSection } from '../types/admin';

interface MainLayoutProps {
  children: React.ReactNode;
}

const THEME_STORAGE_KEY = 'nad_theme';

function getSystemTheme(): 'dark' | 'light' {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: ThemeMode): 'dark' | 'light' {
  return theme === 'system' ? getSystemTheme() : theme;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const {
    activeTab,
    setActiveTab,
    activeAdminSection,
    openAdminSection,
    requestAction,
  } = useNavigation();
  const initials =
    (user?.name || '?')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?';
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
  });
  const [effectiveTheme, setEffectiveTheme] = useState<'dark' | 'light'>(() => resolveTheme(theme));
  const [density, setDensity] = useState<DensityMode>('comfortable');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);

    const apply = () => {
      const resolved = resolveTheme(theme);
      const root = document.documentElement;
      root.classList.toggle('dark', resolved === 'dark');
      root.classList.toggle('light', resolved === 'light');
      setEffectiveTheme(resolved);
    };

    apply();

    if (theme !== 'system') return;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => apply();
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, [theme]);

  const cycleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : prev === 'light' ? 'system' : 'dark'));
  };

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('density-comfortable', 'density-compact');
    root.classList.add(`density-${density}`);
  }, [density]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const handleNavigate = (tab: AppTab) => {
    if (tab === 'admin-config' && !isSidebarOpen) setIsSidebarOpen(true);
    setActiveTab(tab);
    setIsMobileMenuOpen(false);
  };

  const handleAdminNavigate = (section: AdminSection) => {
    openAdminSection(section);
    setIsMobileMenuOpen(false);
  };

  const handleRaiseComplaint = () => {
    requestAction('raise-complaint');
    setActiveTab('complaints');
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    void logout();
  };

  const themeLabel = theme === 'system' ? 'System' : theme === 'dark' ? 'Dark' : 'Light';

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col">
      {/* Top Header */}
      <header className="h-16 shrink-0 border-b border-slate-800 bg-slate-900/80 backdrop-blur-md sticky top-0 z-40 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            aria-label={isSidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            className="hidden md:flex p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          >
            <Menu className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle navigation menu"
            className="flex md:hidden p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Header Actions */}
        <div className="flex items-center gap-2">
          {/* Density Toggle */}
          <button
            onClick={() => setDensity(density === 'comfortable' ? 'compact' : 'comfortable')}
            title={`Switch to ${density === 'comfortable' ? 'Compact' : 'Comfortable'} mode`}
            aria-label={`Switch to ${density === 'comfortable' ? 'Compact' : 'Comfortable'} mode`}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors hidden sm:flex"
          >
            {density === 'comfortable' ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {/* Theme Toggle (Dark / Light / System) */}
          <button
            onClick={cycleTheme}
            title={`Theme: ${themeLabel}`}
            aria-label={`Theme: ${themeLabel}`}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors"
          >
            {theme === 'system' ? <Monitor className="w-4 h-4" /> : effectiveTheme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Notifications */}
          <NotificationBell />

          {/* Profile Badge */}
          <button
            onClick={() => handleNavigate('profile')}
            title="Profile"
            aria-label="View profile"
            aria-current={activeTab === 'profile' ? 'page' : undefined}
            className={clsx(
              'flex items-center gap-2.5 pl-3 ml-1 border-l border-slate-800 transition-colors',
              activeTab === 'profile' ? 'text-sky-400' : 'text-slate-300 hover:text-slate-100'
            )}
          >
            <div className="w-8 h-8 rounded-full bg-sky-500/20 text-sky-400 font-semibold flex items-center justify-center text-xs">
              {initials}
            </div>
            <div className="hidden lg:block text-left">
              <p className="text-xs font-semibold text-slate-200 leading-none">{user?.name || 'Signed in'}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">{user?.email || ''}</p>
            </div>
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside
          className={clsx(
            'hidden md:flex flex-col shrink-0 border-r border-slate-800 bg-slate-900/50 transition-[width] duration-300 motion-reduce:transition-none',
            isSidebarOpen ? 'w-64' : 'w-20'
          )}
        >
          <Sidebar
            role={user?.role}
            activeTab={activeTab}
            activeAdminSection={activeAdminSection}
            collapsed={!isSidebarOpen}
            onNavigate={handleNavigate}
            onAdminNavigate={handleAdminNavigate}
            onRaiseComplaint={handleRaiseComplaint}
            onLogout={handleLogout}
          />
        </aside>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div
              className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            <div className="relative h-full w-72 max-w-[85vw] bg-slate-900 border-r border-slate-800 animate-in slide-in-from-left">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close navigation"
                className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-800 z-10"
              >
                <X className="w-4 h-4" />
              </button>
              <Sidebar
                role={user?.role}
                activeTab={activeTab}
                activeAdminSection={activeAdminSection}
                onNavigate={handleNavigate}
                onAdminNavigate={handleAdminNavigate}
                onRaiseComplaint={handleRaiseComplaint}
                onLogout={handleLogout}
              />
            </div>
          </div>
        )}

        {/* Main Content Workspace */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
